import { useState, useEffect } from 'react';
import { getCallPhoneNumberById } from '../../services/callPhoneNumberService';
import type { CallPhoneNumberHistory } from '../../types/callPhoneNumber';

interface ViewHistoryModalProps {
  callPhoneNumberId: string;
  onClose: () => void;
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit', day: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true,
    }).format(new Date(iso));
  } catch { return iso; }
}

export function ViewHistoryModal({ callPhoneNumberId, onClose }: ViewHistoryModalProps) {
  const [history, setHistory] = useState<CallPhoneNumberHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getCallPhoneNumberById(callPhoneNumberId, 'expand=history')
      .then((result) => setHistory(result.callHistory || []))
      .catch(() => setError('Failed to load history'))
      .finally(() => setLoading(false));
  }, [callPhoneNumberId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        <div className="px-6 pt-6 pb-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Call History</h2>
          {loading && <p className="text-sm text-gray-400">Loading...</p>}
          {error && <p className="text-red-600 text-sm">{error}</p>}
          {!loading && !error && history.length === 0 && (
            <p className="text-sm text-gray-400">No history found.</p>
          )}
        </div>
        {!loading && history.length > 0 && (
          <div className="overflow-y-auto px-6 pb-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                  <th className="pb-2 font-medium">Client Name</th>
                  <th className="pb-2 font-medium">Property Name</th>
                  <th className="pb-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-1.5">{h.clientName}</td>
                    <td className="py-1.5">{h.propertyName}</td>
                    <td className="py-1.5">{formatDate(h.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
